import { defineStore } from 'pinia';
import { erebus, FILE_TRANSFER_TIMEOUT_MS } from 'boot/axios';
import {
  AudioNoteMeta,
  IAudioNoteDescription,
  IAudioNoteMeta,
  ValidationError,
} from 'src/models/audioNotes';

export const useAudioNoteStore = defineStore({
  persist: false,
  id: 'audioNotes',
  state: () => ({
    audioNotes: [] as AudioNoteMeta[],
    audioData: new Map<string, ArrayBuffer>(), // key: noteID, value: ArrayBuffer
    loadingNotes: new Map() as Map<string, Promise<unknown>>,
  }),
  getters: {
    getAudioNoteForCase:
      (state) =>
      (caseID: string): IAudioNoteMeta[] | undefined => {
        return state.audioNotes.filter(
          (an: IAudioNoteMeta) => an.caseID.toLowerCase() === caseID.toLowerCase(),
        );
      },
    getAudioNote:
      (state) =>
      (noteID: string): IAudioNoteMeta | null => {
        const note = state.audioNotes.find(
          (an: IAudioNoteMeta) => an.ID.toLowerCase() === noteID.toLowerCase(),
        );
        return note ?? null;
      },
    getAudioData:
      (state) =>
      (noteID: string): ArrayBuffer | null => {
        const data = state.audioData.get(noteID);
        return data ?? null;
      },
  },
  actions: {
    async fetchCaseAudioNotes(caseID: string): Promise<void> {
      const notes: AudioNoteMeta[] = [];
      const res = await erebus.get(`/audio-notes/case/${caseID}`);
      if (res.data === null || res.data.length <= 0) {
        return; // no audio notes for this case
      }
      res.data.forEach((data: unknown) => {
        if (!AudioNoteMeta.validate(data)) {
          throw new ValidationError('Invalid AudioNoteMeta data');
        }
        notes.push(new AudioNoteMeta(data));
      });
      this.locallySaveAudioNote(notes);
    },
    async fetchAudioNote(noteID: string): Promise<void> {
      const inflight = this.loadingNotes.get(noteID);
      if (inflight) {
        // Another caller is already fetching this note - wait on that request
        await inflight;
        return;
      }
      const promise = erebus.get(`/audio-notes/${noteID}`, {
        responseType: 'arraybuffer',
        timeout: FILE_TRANSFER_TIMEOUT_MS,
      });
      this.loadingNotes.set(noteID, promise);
      try {
        const res = await promise;
        this.audioData.set(noteID, res.data);
      } finally {
        // Clear the in-flight entry even when the request failed. Otherwise
        // the rejected promise stays in the map and every later call waits on
        // it, so the note can never be fetched again this session.
        this.loadingNotes.delete(noteID);
      }
    },
    async postAudioNote(meta: IAudioNoteDescription, data: ArrayBuffer): Promise<void> {
      const formData = new FormData();
      formData.append('meta', JSON.stringify(meta));
      formData.append('file', new Blob([data], { type: 'audio/mp3' }));
      await erebus.post('/audio-notes', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: FILE_TRANSFER_TIMEOUT_MS,
      });
    },
    locallySaveAudioNote(audioNotes: IAudioNoteMeta[]): void {
      this.audioNotes = this.audioNotes.filter((an: IAudioNoteMeta) => {
        return !audioNotes.some((a: IAudioNoteMeta) => a.ID === an.ID);
      });
      this.audioNotes.push(...audioNotes);
    },
  },
});
